import { useState } from "react";
import { Trash2, Download, Sparkles } from "lucide-react";
import {
  useHostImages,
  usePullImage,
  useDeleteImage,
  usePruneImages,
} from "@/hooks/useDocker";

function gb(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} Go`;
  return `${(bytes / 1024 ** 2).toFixed(0)} Mo`;
}

export function ImagesPanel({ hostId }: { hostId: number }) {
  const { data: images, isLoading } = useHostImages(hostId);
  const pull = usePullImage(hostId);
  const del = useDeleteImage(hostId);
  const prune = usePruneImages(hostId);
  const [ref, setRef] = useState("");

  function submitPull() {
    if (!ref) return;
    const [repository, tag] = ref.split(":");
    pull.mutate(
      { repository, tag: tag || undefined },
      { onSuccess: () => setRef("") },
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-200">Images</h2>
        <div className="flex items-center gap-2">
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitPull()}
            placeholder="image:tag (ex: nginx:alpine)"
            className="w-56 rounded border border-gray-700 bg-gray-900 px-2 py-1 text-sm text-gray-100 placeholder-gray-500"
          />
          <button
            onClick={submitPull}
            disabled={!ref || pull.isPending}
            className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {pull.isPending ? "Pull…" : "Pull"}
          </button>
          <button
            onClick={() => {
              if (window.confirm("Supprimer toutes les images inutilisées ?"))
                prune.mutate();
            }}
            className="inline-flex items-center gap-1 rounded border border-gray-700 px-3 py-1 text-sm text-gray-300 hover:bg-gray-800"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Prune
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-800">
        <table className="min-w-full divide-y divide-gray-800 text-sm">
          <thead className="bg-gray-900/60 text-left text-gray-400">
            <tr>
              <th className="px-4 py-2">Tag</th>
              <th className="px-4 py-2">ID</th>
              <th className="px-4 py-2">Taille</th>
              <th className="px-4 py-2">Utilisée</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {(images ?? []).map((img) => (
              <tr key={img.id}>
                <td className="px-4 py-2 font-mono text-gray-100">
                  {img.tags[0] ?? "<none>"}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-500">
                  {img.short_id.replace("sha256:", "")}
                </td>
                <td className="px-4 py-2 text-gray-400">{gb(img.size)}</td>
                <td className="px-4 py-2">
                  {img.in_use ? (
                    <span className="rounded bg-green-900/40 px-2 py-0.5 text-xs text-green-300">
                      en service
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    title="Supprimer l'image"
                    onClick={() => {
                      const force = img.in_use;
                      if (
                        window.confirm(
                          force
                            ? "Image en service — forcer la suppression ?"
                            : `Supprimer ${img.tags[0] ?? img.short_id} ?`,
                        )
                      )
                        del.mutate({ imageId: img.id, force });
                    }}
                    className="rounded p-1 text-gray-400 hover:bg-red-900/40 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && images && images.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-gray-500">
                  Aucune image.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
